import React, { useState } from 'react';
import { useCalculatorContext } from '../contexts';
import tagularEvent from '@/utils/tagular/tagularEvent';
import uuid from '@/utils/uuid';
import { FieldSelected } from '@/types/Tagular';

interface CalculatorInputProps {
  fieldKey: string;
  label: string;
  placeholder: string;
  type: 'text' | 'number';
  prefix?: string;
  helpText?: string;
  required?: boolean;
  validation?: (value: string | number) => string | boolean;
  formatting?: (value: string | number | null) => string;
  className?: string;
  trackingEnabled?: boolean;
  isEditable?: boolean;
  viewCorrelationId?: string;
}

const CalculatorInput: React.FC<CalculatorInputProps> = ({
  fieldKey,
  label,
  placeholder,
  type,
  prefix,
  helpText,
  required = false,
  validation,
  formatting,
  className = '',
  trackingEnabled = true,
  isEditable = false,
  viewCorrelationId,
}) => {
  const { state, updateCalculatorValue, config } = useCalculatorContext();
  const [error, setError] = useState<string>('');

  // Get current value from calculator state
  const currentValue = state.calculatorValues[fieldKey] || '';

  // Check if field should be disabled based on editability
  const isDisabled = !isEditable;

  // Track field input with Tagular
  const trackFieldInput = (value: any) => {
    if (!trackingEnabled) return;

    const correlationId = viewCorrelationId || uuid();
    const fieldPayload: FieldSelected = {
      correlationId,
      formContext: {
        formId: config?.id || '',
        formName: config?.tracking?.formName || '',
        formType: config?.tracking?.formType || '',
        formVersion: '1.0',
      },
      userInputField: {
        fieldName: fieldKey,
        fieldType: fieldKey,
        fieldValue: String(value || ''),
        fieldLabel: label,
      },
    };
    tagularEvent('FieldSelected', fieldPayload);
  };

  // Validate field value
  const validateField = (value: string | number): string => {
    if (required && (!value || value === '')) {
      return `${label} is required`;
    }

    if (validation && value) {
      const result = validation(value);
      if (typeof result === 'string') {
        return result;
      }
      if (result === false) {
        return `Invalid ${label}`;
      }
    }

    return '';
  };

  // Format field value for display
  const formatFieldValue = (value: string | number): string => {
    if (!value) return '';

    if (formatting) {
      return formatting(value as string | number | null);
    }

    if (type === 'number' && prefix === '$') {
      return new Intl.NumberFormat().format(value as number);
    }

    return String(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isDisabled) return;

    const newValue = e.target.value;

    updateCalculatorValue(fieldKey, newValue);

    // Validate the field
    const errorMessage = validateField(newValue);
    setError(errorMessage);

    // Track field input
    if (newValue) {
      trackFieldInput(newValue);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (currentValue && formatting) {
      e.target.value = formatFieldValue(currentValue);
    }
  };

  const renderInput = () => {
    if (prefix === '$') {
      // Special handling for currency fields
      return (
        <div
          className={`border border-gray-200 rounded-lg transition-all duration-200 ${
            !isEditable || isDisabled
              ? 'bg-gray-25 cursor-default px-3 py-2'
              : 'bg-white cursor-text hover:shadow-sm h-11 flex px-4 py-2 items-center'
          }`}
        >
          {!isEditable && (
            <div className='font-bold text-gray-900 text-sm overflow-hidden whitespace-nowrap text-ellipsis'>
              {label}
            </div>
          )}
          <div className='relative flex items-center w-full'>
            <div className='absolute left-0 flex items-center pointer-events-none'>
              <span className='text-gray-500 text-sm'>$</span>
            </div>
            <input
              id={fieldKey}
              name={fieldKey}
              type='text'
              className={`w-full p-0 bg-transparent border-none outline-none text-sm ${
                currentValue ? 'text-gray-700 font-medium' : 'text-gray-500'
              }`}
              placeholder={placeholder}
              value={currentValue ? formatFieldValue(currentValue) : ''}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isDisabled}
            />
          </div>
        </div>
      );
    }

    return (
      <div
        className={`border border-gray-200 rounded-lg transition-all duration-200 ${
          !isEditable || isDisabled
            ? 'bg-gray-25 cursor-default px-3 py-2'
            : 'bg-white cursor-text hover:shadow-sm h-11 flex px-4 py-2 items-center'
        }`}
      >
        {!isEditable && (
          <div className='font-bold text-gray-900 text-sm overflow-hidden whitespace-nowrap text-ellipsis'>
            {label}
          </div>
        )}
        <div className='flex items-center justify-between w-full'>
          <input
            id={fieldKey}
            name={fieldKey}
            type={type === 'number' ? 'number' : 'text'}
            className={`w-full p-0 bg-transparent border-none outline-none text-sm overflow-hidden whitespace-nowrap text-ellipsis flex-1 ${
              currentValue ? 'text-gray-700 font-medium' : 'text-gray-500'
            }`}
            placeholder={placeholder}
            value={String(currentValue || '')}
            onChange={handleChange}
            disabled={isDisabled}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={className}>
      {isEditable && (
        <div className='font-bold text-sm text-gray-900 pb-1'>{label}</div>
      )}

      {renderInput()}
    </div>
  );
};

export default CalculatorInput;
